import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Download, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  DespachoInfoCallout,
  DespachoKpiCard,
  DespachoPageShell,
  DespachoSectionCard,
  DespachoSimpleTable,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  OPERATIONAL_DASHBOARD_CARDS,
  OPERATIONAL_REPORT_TABS,
  filterOperationalCardsForScope,
  filterOperationalTabsForScope,
  operationalReportsApi,
  resolveOperationalReportTab,
} from "@/modules/despacho/operationalReportsApi";
import {
  CLIENT_GROUPED_REPORT_IDS,
  groupOperationalItemsByClient,
  pendingItemMeta,
  pendingItemTitle,
} from "@/modules/despacho/groupOperationalItemsByClient";
import { buildClientesDeepLink } from "@/lib/clientesDeepLink";
import { parseFiscalInfoError } from "@/modules/despacho/despachoFiscalInfoApi";
import {
  isDespachoReadOnly,
} from "@/modules/despacho/despachoPermissions";
import { activeStaffForAssignment, isDespachoAssignmentScopedUser } from "@/modules/despacho/despachoAssignmentsHelpers";
import {
  FISCAL_BOARD_STAFF_FILTER_ALL,
  isFiscalBoardStaffFilterAll,
} from "@/modules/despacho/despachoFiscalBoardHelpers";
import { despachoStaffApi } from "@/modules/despacho/despachoStaffApi";
import { despachoRoleLabel } from "@/modules/despacho/despachoStaffHelpers";
import TaskDetailDialog from "@/pages/despacho/TaskDetailDialog";
import ComplianceInstanceDetailDialog from "@/pages/despacho/ComplianceInstanceDetailDialog";
import { useAuth } from "@/lib/auth";
import { useCapabilities } from "@/lib/useCapabilities";

const BADGE_LABELS = {
  falta_cfs: "Sin CFS",
  sin_obligaciones: "Sin obligaciones",
  revisar_cfs: "Revisar CFS",
  desactualizado: "CFS desactualizado",
  requiere_revision: "Requiere revisión",
  completo: "Completo",
};

const VALID_REPORT_IDS = new Set(OPERATIONAL_REPORT_TABS.map((t) => t.id));

function reportColumns(reportId) {
  switch (reportId) {
    case "upcoming_due_dates":
    case "compliance_by_obligation":
      return [
        { key: "fecha_fiscal", label: "Fecha fiscal" },
        { key: "client_name", label: "Cliente" },
        { key: "client_rfc", label: "RFC" },
        { key: "obligation_name", label: "Obligación" },
        { key: "period", label: "Periodo" },
        { key: "assigned_auxiliar_name", label: "Auxiliar" },
        {
          key: "status_label",
          label: "Estado",
          render: (r) => <DespachoStatusBadge variant="secondary">{r.status_label || r.status}</DespachoStatusBadge>,
        },
        { key: "days_remaining", label: "Días restantes", render: (r) => r.days_remaining ?? "—" },
      ];
    case "pending_tasks":
      return [
        { key: "due_date", label: "Fecha" },
        { key: "client_name", label: "Cliente" },
        { key: "task_name", label: "Tarea", render: (r) => r.task_name || r.title },
        { key: "assigned_to_name", label: "Auxiliar" },
        {
          key: "status_label",
          label: "Estado",
          render: (r) => <DespachoStatusBadge variant="secondary">{r.status_label || r.status}</DespachoStatusBadge>,
        },
      ];
    case "overdue":
      return [
        { key: "client_name", label: "Cliente" },
        { key: "title", label: "Obligación / tarea" },
        { key: "period", label: "Periodo" },
        {
          key: "due",
          label: "Fecha",
          render: (r) => r.fecha_fiscal || r.due_date || "—",
        },
        { key: "days_overdue", label: "Días vencidos" },
        {
          key: "assignee",
          label: "Auxiliar",
          render: (r) => r.assigned_auxiliar_name || r.assigned_to_name || "—",
        },
        {
          key: "status_label",
          label: "Estado",
          render: (r) => <DespachoStatusBadge variant="secondary">{r.status_label || r.status}</DespachoStatusBadge>,
        },
      ];
    case "obligations_by_client":
      return [
        { key: "client_name", label: "Cliente" },
        { key: "client_rfc", label: "RFC" },
        { key: "obligation_name", label: "Obligación" },
        { key: "authority", label: "Autoridad" },
        { key: "source", label: "Fuente" },
        { key: "status", label: "Estado" },
        { key: "assigned_auxiliar_name", label: "Auxiliar" },
      ];
    case "tasks_by_auxiliar":
      return [
        { key: "auxiliar_name", label: "Auxiliar" },
        { key: "clients_assigned", label: "Clientes" },
        { key: "total", label: "Total" },
        { key: "pending", label: "Pendientes" },
        { key: "in_progress", label: "En proceso" },
        { key: "overdue", label: "Vencidas" },
        { key: "done", label: "Terminadas" },
      ];
    case "not_applicable_this_month":
      return [
        { key: "client_name", label: "Cliente" },
        { key: "obligation_name", label: "Obligación" },
        { key: "period", label: "Periodo" },
        { key: "assigned_auxiliar_name", label: "Auxiliar" },
        { key: "updated_at", label: "Fecha cierre", render: (r) => String(r.updated_at || "").slice(0, 10) },
        { key: "evidence_comment", label: "Comentario", render: (r) => r.evidence_comment || r.comment || "—" },
      ];
    case "alerts_reminders":
      return [
        { key: "scheduled_for", label: "Fecha" },
        { key: "alert_type", label: "Tipo" },
        { key: "client_name", label: "Cliente" },
        { key: "message", label: "Mensaje", render: (r) => (r.message || "").slice(0, 80) },
        { key: "status", label: "Estado" },
      ];
    case "client_messages":
      return [
        { key: "fiscal_client_id", label: "Cliente" },
        { key: "message_type", label: "Tipo", render: (r) => r.message_type || r.subject || "—" },
        { key: "period", label: "Periodo" },
        { key: "status", label: "Estado" },
        { key: "body", label: "Mensaje", render: (r) => (r.body || "").slice(0, 80) },
      ];
    case "incomplete_clients":
      return [
        { key: "client_name", label: "Cliente" },
        { key: "client_rfc", label: "RFC" },
        { key: "regimen_fiscal", label: "Régimen" },
        { key: "assigned_auxiliar_name", label: "Auxiliar" },
        {
          key: "fiscal_status_badge",
          label: "Estado fiscal",
          render: (r) => BADGE_LABELS[r.fiscal_status_badge] || r.fiscal_status_badge || "—",
        },
      ];
    default:
      return [{ key: "id", label: "Registro" }];
  }
}

function CompanyGroupedPendingList({
  reportId,
  items,
  onOpenTask,
  onOpenInstance,
}) {
  const groups = useMemo(() => groupOperationalItemsByClient(items), [items]);

  if (!groups.length) {
    return <p className="text-sm text-slate-500 py-4 m-0">Sin registros con los filtros actuales.</p>;
  }

  return (
    <Accordion
      type="multiple"
      className="rounded-sm border border-slate-200 bg-white divide-y divide-slate-100"
      data-testid="tablero-operativo-por-empresa"
    >
      {groups.map((group) => (
        <AccordionItem key={group.key} value={group.key} className="border-0 px-3">
          <AccordionTrigger className="text-sm font-semibold text-[#0F2D5C] hover:no-underline py-3">
            <span className="flex flex-1 items-center justify-between gap-2 pr-2 min-w-0 text-left">
              <span className="min-w-0 truncate">
                {group.client_name}
                {group.client_rfc ? (
                  <span className="ml-2 font-normal text-slate-500 text-xs">{group.client_rfc}</span>
                ) : null}
              </span>
              <span className="shrink-0 rounded-sm bg-slate-100 text-slate-800 text-[10px] font-medium px-2 py-0.5">
                {group.items.length} pendiente{group.items.length === 1 ? "" : "s"}
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pb-3">
            <ul className="m-0 p-0 list-none space-y-2">
              {group.items.map((row, idx) => {
                const title = pendingItemTitle(row);
                const meta = pendingItemMeta(row, reportId);
                const isTask = row.kind === "task" || row.despacho_task_id;
                const instanceId = row.compliance_instance_id || (row.kind === "compliance" ? row.id : null);
                return (
                  <li
                    key={row.id || `${group.key}-${idx}`}
                    className="rounded-sm border border-slate-200 bg-slate-50/80 px-3 py-2 text-xs"
                  >
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="m-0 font-medium text-slate-900">{title}</p>
                        {meta ? <p className="m-0 mt-0.5 text-slate-500">{meta}</p> : null}
                      </div>
                      <div className="flex flex-wrap gap-1 shrink-0">
                        {isTask ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7"
                            onClick={() => onOpenTask?.(row.id)}
                          >
                            Ver tarea
                          </Button>
                        ) : null}
                        {instanceId ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-7"
                            onClick={() => onOpenInstance?.(instanceId)}
                          >
                            Ver instancia
                          </Button>
                        ) : null}
                        {group.fiscal_client_id ? (
                          <Button asChild type="button" size="sm" variant="ghost" className="h-7">
                            <Link to={buildClientesDeepLink({ clientId: group.fiscal_client_id, tab: "obligaciones" })}>
                              Expediente
                            </Link>
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

function OperationalReportPanel({
  reportId,
  extraParams = {},
  readOnly = false,
  preferGrouped = true,
  canToggleDetail = false,
  onOpenTask,
  onOpenInstance,
}) {
  const [items, setItems] = useState([]);
  const [loadState, setLoadState] = useState("idle");
  const [exportBusy, setExportBusy] = useState(false);
  const [showDetailTable, setShowDetailTable] = useState(false);

  const paramsKey = JSON.stringify(extraParams || {});

  const load = useCallback(async () => {
    setLoadState("loading");
    try {
      const params = paramsKey ? JSON.parse(paramsKey) : {};
      const data = await operationalReportsApi.getReport(reportId, { limit: 500, ...params });
      setItems(Array.isArray(data?.items) ? data.items : []);
      setLoadState("ready");
    } catch (err) {
      setItems([]);
      setLoadState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  }, [reportId, paramsKey]);

  useEffect(() => {
    load();
  }, [load]);

  const useGrouped = preferGrouped && CLIENT_GROUPED_REPORT_IDS.has(reportId) && !showDetailTable;

  const columns = useMemo(() => {
    const base = reportColumns(reportId);
    if (reportId === "overdue" || reportId === "upcoming_due_dates" || reportId === "compliance_by_obligation" || reportId === "pending_tasks") {
      return [
        ...base,
        {
          key: "_act",
          label: "Acción",
          render: (r) => (
            <div className="flex gap-1">
              {r.kind === "task" || r.despacho_task_id ? (
                <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => onOpenTask?.(r.id)}>
                  Ver tarea
                </Button>
              ) : null}
              {r.compliance_instance_id || (r.kind === "compliance" && r.id) ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7"
                  onClick={() => onOpenInstance?.(r.compliance_instance_id || r.id)}
                >
                  Ver instancia
                </Button>
              ) : null}
            </div>
          ),
        },
      ];
    }
    if (reportId === "incomplete_clients") {
      return [
        ...base,
        {
          key: "_link",
          label: "Acción",
          render: (r) => (
            <Button asChild type="button" size="sm" variant="ghost" className="h-7">
              <Link to={buildClientesDeepLink({ clientId: r.fiscal_client_id, tab: "obligaciones" })}>
                Ver expediente
              </Link>
            </Button>
          ),
        },
      ];
    }
    return base;
  }, [reportId, onOpenTask, onOpenInstance]);

  const handleExport = async () => {
    if (readOnly) return;
    setExportBusy(true);
    try {
      const params = paramsKey ? JSON.parse(paramsKey) : {};
      await operationalReportsApi.exportReport(reportId, { limit: 2000, ...params });
      toast.success("Exportación descargada.");
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setExportBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 justify-end">
        {canToggleDetail && CLIENT_GROUPED_REPORT_IDS.has(reportId) ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm h-8"
            onClick={() => setShowDetailTable((v) => !v)}
          >
            {showDetailTable ? "Vista por empresa" : "Vista detallada"}
          </Button>
        ) : null}
        <Button type="button" variant="outline" size="sm" className="rounded-sm h-8" onClick={load} disabled={loadState === "loading"}>
          Actualizar
        </Button>
        {!readOnly ? (
          <Button type="button" variant="secondary" size="sm" className="rounded-sm h-8" onClick={handleExport} disabled={exportBusy}>
            {exportBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" aria-hidden /> : <Download className="h-3 w-3 mr-1" aria-hidden />}
            Exportar CSV
          </Button>
        ) : null}
      </div>
      {loadState === "loading" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando tablero…
        </div>
      ) : null}
      {loadState === "ready" && useGrouped ? (
        <CompanyGroupedPendingList
          reportId={reportId}
          items={items}
          onOpenTask={onOpenTask}
          onOpenInstance={onOpenInstance}
        />
      ) : null}
      {loadState === "ready" && !useGrouped ? (
        <DespachoSimpleTable
          columns={columns}
          rows={items.map((r, i) => ({ ...r, id: r.id || `${reportId}-${i}` }))}
          emptyLabel="Sin registros con los filtros actuales."
        />
      ) : null}
    </div>
  );
}

export default function ReportesDespacho() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { permissions, role, isPlatformDesigner, loading: capabilitiesLoading } = useCapabilities();
  const readOnly = isDespachoReadOnly({ permissions, role, isPlatformDesigner, capabilitiesLoading, user });
  const scopedStaffUser = isDespachoAssignmentScopedUser(user);
  const [searchParams, setSearchParams] = useSearchParams();

  const [dashboard, setDashboard] = useState(null);
  const [dashState, setDashState] = useState("loading");
  const reportFromUrl = searchParams.get("report");
  const auxiliarFromUrl = searchParams.get("auxiliar");
  const [activeTab, setActiveTab] = useState(() => {
    const resolved = resolveOperationalReportTab(reportFromUrl);
    return VALID_REPORT_IDS.has(resolved.tab) ? resolved.tab : "overdue";
  });
  const [selectedAuxId, setSelectedAuxId] = useState(() => {
    if (scopedStaffUser) return String(user?.id || "");
    if (auxiliarFromUrl && auxiliarFromUrl !== FISCAL_BOARD_STAFF_FILTER_ALL) return auxiliarFromUrl;
    return FISCAL_BOARD_STAFF_FILTER_ALL;
  });
  const [staffOptions, setStaffOptions] = useState([]);
  const [cardFilters, setCardFilters] = useState({});
  const [taskDetailId, setTaskDetailId] = useState(null);
  const [instanceDetailId, setInstanceDetailId] = useState(null);

  const assignedAuxiliarId = useMemo(
    () => (isFiscalBoardStaffFilterAll(selectedAuxId) ? undefined : selectedAuxId),
    [selectedAuxId],
  );

  const loadDashboard = useCallback(async () => {
    setDashState("loading");
    try {
      const params = assignedAuxiliarId ? { assigned_auxiliar_id: assignedAuxiliarId } : {};
      const data = await operationalReportsApi.getDashboard(params);
      setDashboard(data);
      setDashState("ready");
    } catch (err) {
      setDashboard(null);
      setDashState("error");
      toast.error(parseFiscalInfoError(err).message);
    }
  }, [assignedAuxiliarId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    // Cargar catálogo de auxiliares para el select (admin/supervisor).
    if (scopedStaffUser) return undefined;
    let cancelled = false;
    despachoStaffApi
      .list()
      .then((staff) => {
        if (!cancelled) setStaffOptions(activeStaffForAssignment(staff));
      })
      .catch(() => {
        if (!cancelled) setStaffOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [scopedStaffUser]);

  /** Bookmarks / Asistente con ?report= antiguo → pestaña vigente o ruta externa. */
  useEffect(() => {
    const report = searchParams.get("report");
    if (!report) return;
    const resolved = resolveOperationalReportTab(report);
    if (resolved.href) {
      navigate(resolved.href, { replace: true });
      return;
    }
    if (VALID_REPORT_IDS.has(resolved.tab)) {
      setActiveTab(resolved.tab);
      if (resolved.tab !== report) {
        const next = new URLSearchParams(searchParams);
        next.set("report", resolved.tab);
        setSearchParams(next, { replace: true });
      }
    }
  }, [searchParams, navigate, setSearchParams]);

  useEffect(() => {
    const aux = searchParams.get("auxiliar");
    if (scopedStaffUser) return;
    if (!aux) return;
    if (aux === FISCAL_BOARD_STAFF_FILTER_ALL || aux === "all") {
      setSelectedAuxId(FISCAL_BOARD_STAFF_FILTER_ALL);
    } else {
      setSelectedAuxId(aux);
    }
  }, [searchParams, scopedStaffUser]);

  const canViewTeam = Boolean(
    dashboard?.can_view_team_reports
    || dashboard?.scope === "team",
  );

  const auxSelectOptions = useMemo(
    () => staffOptions.map((s) => ({
      id: s.id,
      label: `${s.name || s.email || s.id} · ${despachoRoleLabel(s.despacho_role)}`,
    })),
    [staffOptions],
  );

  const selectedAuxLabel = useMemo(() => {
    if (!assignedAuxiliarId) return null;
    const opt = auxSelectOptions.find((o) => o.id === assignedAuxiliarId);
    return opt?.label || assignedAuxiliarId;
  }, [assignedAuxiliarId, auxSelectOptions]);

  const reportExtraParams = useMemo(() => {
    const base = { ...(cardFilters || {}) };
    if (canViewTeam && assignedAuxiliarId) {
      base.assigned_auxiliar_id = assignedAuxiliarId;
    }
    return base;
  }, [cardFilters, canViewTeam, assignedAuxiliarId]);

  const visibleTabs = useMemo(
    () => filterOperationalTabsForScope(OPERATIONAL_REPORT_TABS, { canViewTeamReports: canViewTeam }),
    [canViewTeam],
  );

  const visibleCards = useMemo(
    () => filterOperationalCardsForScope(OPERATIONAL_DASHBOARD_CARDS, { canViewTeamReports: canViewTeam }),
    [canViewTeam],
  );

  useEffect(() => {
    if (!visibleTabs.length) return;
    if (!visibleTabs.some((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab]);

  const syncAuxiliarQuery = (nextAuxId) => {
    const next = new URLSearchParams(searchParams);
    if (isFiscalBoardStaffFilterAll(nextAuxId)) {
      next.delete("auxiliar");
    } else {
      next.set("auxiliar", nextAuxId);
    }
    setSearchParams(next, { replace: true });
  };

  const handleAuxiliarChange = (value) => {
    setSelectedAuxId(value);
    syncAuxiliarQuery(value);
  };

  const handleCardClick = (card) => {
    setActiveTab(card.report);
    setCardFilters(card.params || {});
    const next = new URLSearchParams(searchParams);
    next.set("report", card.report);
    setSearchParams(next, { replace: true });
  };

  const handleTabChange = (v) => {
    setActiveTab(v);
    setCardFilters({});
    const next = new URLSearchParams(searchParams);
    next.set("report", v);
    setSearchParams(next, { replace: true });
  };

  const kpis = dashboard?.kpis || {};

  const scopeDescription = (() => {
    if (dashboard?.scope !== "team") return "mis asignaciones";
    if (selectedAuxLabel) return `auxiliar: ${selectedAuxLabel.split(" · ")[0]}`;
    return "equipo completo";
  })();

  return (
    <DespachoPageShell
      title="Tablero operativo"
      subtitle="Pendientes por empresa: vencidos, esta semana, tareas y clientes incompletos."
      testId="despacho-reportes"
    >
      <DespachoInfoCallout testId="despacho-reportes-notice">
        Vista simplificada: cada empresa aparece una vez con el listado de lo pendiente.
        Al abrir un ítem se muestra el detalle (tarea o instancia). No se envían mensajes automáticos a clientes.
      </DespachoInfoCallout>

      {dashState === "loading" ? (
        <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Cargando tablero…
        </div>
      ) : null}

      {dashState === "ready" && dashboard ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="operational-dashboard-cards">
            {visibleCards.map((card) => (
              <button
                key={card.key}
                type="button"
                className="text-left rounded-sm border border-slate-200 bg-white hover:border-[#0F2D5C]/30 transition-colors overflow-hidden"
                onClick={() => handleCardClick(card)}
              >
                <DespachoKpiCard label={card.label} value={kpis[card.key] ?? 0} tone={card.tone} />
              </button>
            ))}
          </div>

          {canViewTeam ? (
            <div className="grid grid-cols-2 sm:grid-cols-2 gap-2 mt-2">
              <DespachoKpiCard label="Completas" value={kpis.clients_complete ?? 0} tone="info" />
              <DespachoKpiCard label="Empresas en tablero" value={kpis.total_clients ?? 0} tone="neutral" />
            </div>
          ) : null}

          <DespachoSectionCard
            title="Pendientes"
            description={`Alcance: ${scopeDescription}. Elige un auxiliar o «Todos» para revisar la carga de cada uno.`}
            className="mt-4"
            testId="despacho-reportes-pendientes"
            headerActions={
              canViewTeam ? (
                <div className="min-w-[220px] max-w-[320px]" data-testid="despacho-reportes-aux-filter-row">
                  <Label htmlFor="despacho-reportes-aux-filter" className="text-xs text-slate-600">
                    Auxiliar
                  </Label>
                  <Select
                    value={selectedAuxId || FISCAL_BOARD_STAFF_FILTER_ALL}
                    onValueChange={handleAuxiliarChange}
                    disabled={scopedStaffUser}
                  >
                    <SelectTrigger
                      id="despacho-reportes-aux-filter"
                      className="rounded-sm h-9 text-sm mt-0.5 bg-white min-w-[220px]"
                      data-testid="despacho-reportes-aux-filter"
                    >
                      <SelectValue placeholder="Todos los auxiliares" />
                    </SelectTrigger>
                    <SelectContent>
                      {!scopedStaffUser ? (
                        <SelectItem
                          value={FISCAL_BOARD_STAFF_FILTER_ALL}
                          data-testid="despacho-reportes-aux-filter-all"
                        >
                          Todos los auxiliares
                        </SelectItem>
                      ) : null}
                      {auxSelectOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null
            }
          >
            <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
              <TabsList className="rounded-sm h-auto flex-wrap gap-1 bg-slate-100 p-1">
                {visibleTabs.map((tab) => (
                  <TabsTrigger key={tab.id} value={tab.id} className="rounded-sm text-xs">
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
              {visibleTabs.map((tab) => (
                <TabsContent key={tab.id} value={tab.id} className="mt-0">
                  <OperationalReportPanel
                    reportId={tab.id}
                    extraParams={activeTab === tab.id ? reportExtraParams : {}}
                    readOnly={readOnly}
                    preferGrouped
                    canToggleDetail={canViewTeam}
                    onOpenTask={setTaskDetailId}
                    onOpenInstance={setInstanceDetailId}
                  />
                </TabsContent>
              ))}
            </Tabs>
          </DespachoSectionCard>

          <div className="flex flex-wrap gap-2 text-xs text-slate-600">
            <Button asChild variant="outline" size="sm" className="rounded-sm h-8">
              <Link to="/clientes">
                Clientes — expediente fiscal
                <ExternalLink className="h-3 w-3 ml-1" aria-hidden />
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-sm h-8">
              <Link to="/despacho/tareas">Tareas</Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-sm h-8">
              <Link to="/despacho/calendario">Calendario</Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-sm h-8">
              <Link to="/despacho/mensajes">Atención a clientes</Link>
            </Button>
          </div>
        </>
      ) : null}

      <TaskDetailDialog
        taskId={taskDetailId}
        open={Boolean(taskDetailId)}
        onOpenChange={(open) => !open && setTaskDetailId(null)}
        readOnly={readOnly}
      />
      <ComplianceInstanceDetailDialog
        instanceId={instanceDetailId}
        open={Boolean(instanceDetailId)}
        onOpenChange={(open) => !open && setInstanceDetailId(null)}
        readOnly={readOnly}
      />
    </DespachoPageShell>
  );
}
